import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mountain, Eye, EyeOff, Download, Settings } from 'lucide-react';

const ContourControls = ({ 
  onGenerateContours, 
  onToggleTriangulation,
  onToggleContours,
  onTogglePointLabels,
  onExportDXF,
  showTriangulation,
  showContours,
  showPointLabels,
  hasPoints,
  hasContours 
}) => {
  const [interval, setInterval] = useState(1.0);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateContours = async () => {
    if (interval <= 0) return;
    
    setIsGenerating(true);
    await onGenerateContours(interval);
    setIsGenerating(false);
  };

  return (
    <motion.div 
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-6 shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Generación de curvas */}
        <div className="lg:w-1/3">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            <Mountain className="w-4 h-4 inline mr-2" />
            Curvas de Nivel
          </label>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Equidistancia (m)
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={interval}
                onChange={(e) => setInterval(parseFloat(e.target.value) || 1)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-sm"
              />
            </div>
            <motion.button
              onClick={handleGenerateContours}
              disabled={!hasPoints || isGenerating}
              className={`w-full px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                hasPoints && !isGenerating
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              whileHover={hasPoints && !isGenerating ? { scale: 1.02 } : {}}
              whileTap={hasPoints && !isGenerating ? { scale: 0.98 } : {}}
            >
              {isGenerating ? 'Generando...' : 'Generar Curvas'}
            </motion.button>
          </div>
        </div>

        {/* Controles de visualización */}
        <div className="lg:w-1/3">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            <Settings className="w-4 h-4 inline mr-2" />
            Visualización
          </label>
          <div className="space-y-2">
            <motion.button
              onClick={onTogglePointLabels}
              className={`w-full px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                showPointLabels
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {showPointLabels ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Etiquetas de Puntos
            </motion.button>
            
            <motion.button
              onClick={onToggleTriangulation}
              className={`w-full px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                showTriangulation
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {showTriangulation ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Triangulación
            </motion.button>
            
            <motion.button
              onClick={onToggleContours}
              className={`w-full px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                showContours
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {showContours ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Curvas de Nivel
            </motion.button>
          </div>
        </div>

        {/* Exportación */}
        <div className="lg:w-1/3">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            <Download className="w-4 h-4 inline mr-2" />
            Exportación
          </label>
          <div className="space-y-3">
            <motion.button
              onClick={onExportDXF}
              disabled={!hasPoints}
              className={`w-full px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                hasPoints
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              whileHover={hasPoints ? { scale: 1.02 } : {}}
              whileTap={hasPoints ? { scale: 0.98 } : {}}
            >
              <Download className="w-4 h-4 inline mr-2" />
              Exportar DXF
            </motion.button>
            
            {hasContours && (
              <div className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                ✓ Curvas de nivel generadas
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContourControls;